import React, { useContext, useEffect, useRef, useState } from 'react';
import IPage from '../../interfaces/IPage';
import { useInternalUserService } from '../../hooks/services/useInternalUserService';
import IInternalUser from '../../interfaces/IInternalUser';
import { Container } from './InternalUserList.styled';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { BiAddToQueue } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { AiTwotoneEdit } from 'react-icons/ai';
import { BsTrash } from 'react-icons/bs';
import { SiOpenaccess } from 'react-icons/si';
import { IoNewspaper } from 'react-icons/io5';
import CustomTableSortLabel from '../../components/custom-table-sort-label/CustomTableSortLabel';
import CustomTablePagination from '../../components/custom-table-pagination/CustomTablePagination';
import moment from 'moment';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import InternalUserTypeDocumentsAccess from './internalUserTypeDocumentsAcess';
import Transition from '../followup/FollowupTransition';
import { useTenantService } from '../../hooks/services/useTenantService';
import Tenant from '../../interfaces/ITenant';
import { hasAccessToAction } from '../../util/hasAccessTo';
import UserContext from '../../context/user/UserContext';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const InternalUserList = (props: IPage) => {
  window.document.title = props.title;

  const { getTenant } = useTenantService();

  const timeout = useRef<any>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('createdAt');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<IInternalUser[]>([]);
  const [tenant, setTenant] = useState<Tenant>();
  const [openUserTypeDocuments, setOpenUserTypeDocuments] = useState(false);
  const [userIdForDialog, setUserIdForDialog] = useState<string>('');
  const [userNameIdForDialog, setUserNameForDialog] = useState<string>('');
  const [filterField, setFilterField] = useState<string>('');
  const [filterPrecision, setFilterPrecision] = useState<string>('');
  const [filterValue, setFilterValue] = useState<string>('');
  const [fields] = useState<{ field: string; label: string }[]>([
    {
      field: 'code',
      label: 'Código',
    },
    {
      field: 'userName',
      label: 'Login',
    },
    {
      field: 'fullName',
      label: 'Nome Completo',
    },
    {
      field: 'email',
      label: 'E-mail',
    },
  ]);

  const userService = useInternalUserService();
  const navigate = useNavigate();
  const toastr = useToastrVanila();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;

  const isEdit = hasAccessToAction(
    ["manutencao-usuario-atestado-web"],
    resourcesWithActions,
    "Editar",
  );

  const listUsers = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: 'asc' | 'desc',
  ) => {
    setLoading(true);

    let url = `perPage=${perPage}&currentPage=${currentPage +
      1}&orderBy=${orderField}&orderDirection=${orderDirection}`;

    if (filterField && filterValue && filterPrecision) {
      url = url + `&filterField=${filterField}&filterValue=${filterValue}&precision=${filterPrecision}`;
    }
    await userService
      .list(url)
      .then(result => {
        setTotalRows(result.totalRows);
        setUsers(result.data);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const listUserDebounced = () => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(async () => {
      await listUsers(perPage, currentPage, orderField, orderDirection);
    }, 500);
  };

  const handleGetTenant = async () => {
    await getTenant()
      .then(result => {
        setTenant(result);
      })
      .catch(error => {
        toastr.error(error);
      });
  };

  const remove = async (id: string) => {
    setLoading(true);
    await userService
      .remove(id)
      .then(async result => {
        toastr.success('Usuário excluído com sucesso!');
        await listUsers(perPage, currentPage, orderField, orderDirection);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
  };

  const openUserTypeDocumentsForm = (userId: string, userName: string) => {
    setUserIdForDialog(userId);
    setUserNameForDialog(userName);
    setOpenUserTypeDocuments(true);
  };

  const hasActiveFilter = Boolean(filterField && filterValue && filterPrecision);

  const resultCountLabel = hasActiveFilter
    ? `${totalRows} ${totalRows === 1 ? 'usuário corresponde' : 'usuários correspondem'} ao filtro`
    : `${totalRows} ${totalRows === 1 ? 'usuário cadastrado' : 'usuários cadastrados'}`;

  useEffect(() => {
    listUserDebounced();
  }, [filterField, filterValue, filterPrecision]);

  useEffect(() => {
    listUsers(perPage, currentPage, orderField, orderDirection);
    handleGetTenant();
  }, []);

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          marginTop: '10px',
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, color: 'text.primary' }}>
          <SupervisorAccountIcon />
          Listagem de usuários
        </Typography>
        <Tooltip title="Cadastrar novo" placement="left-start">
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<BiAddToQueue />}
              onClick={() => navigate(`/user/${0}/new`)}
              disabled={!isEdit}
            >
              Cadastrar usuário
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="select-filter">Filtrar por</InputLabel>
            <Select
              label="Filtrar Por"
              value={`${filterField}`}
              onChange={e => {
                setFilterField(`${e.target.value}`);
              }}
            >
              {fields.map(filter => {
                return (
                  <MenuItem key={filter.field} value={filter.field}>
                    {filter.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Precisão</InputLabel>
            <Select
              label="Precisão"
              value={`${filterPrecision}`}
              onChange={e => {
                setFilterPrecision(`${e.target.value}`);
              }}
            >
              <MenuItem key={'equal'} value={'equal'}>
                Igual
              </MenuItem>
              <MenuItem key={'containing'} value={'containing'}>
                Contendo
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={7}>
          <TextField
            fullWidth
            label="Valor a filtrar"
            placeholder="Valor a filtrar"
            value={filterValue}
            onChange={handleTextFilterChange}
            size="small"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: 2, color: 'text.secondary' }}>
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="body2">{resultCountLabel}</Typography>
      </Box>

      <TableContainer sx={{ marginTop: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="code"
                label="Código"
                handleRequest={listUsers}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="userName"
                label="Login"
                handleRequest={listUsers}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="fullName"
                label="Nome Completo"
                handleRequest={listUsers}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="email"
                label="E-mail"
                handleRequest={listUsers}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="createdAt"
                label="Data de Cadastro"
                handleRequest={listUsers}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="lastAccess"
                label="Último acesso"
                handleRequest={listUsers}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                sx={{ whiteSpace: 'nowrap' }}
              />
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users &&
              users.map(user => {
                return (
                  <TableRow key={user.id} hover={true}>
                    <TableCell>{user.code}</TableCell>
                    <TableCell>{user.userName}</TableCell>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{moment(`${user.createdAt}`).format('DD/MM/yyyy HH:mm')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.lastAccess ? moment(`${user.lastAccess}`).format('DD/MM/yyyy HH:mm') : '-'}
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton color="info" onClick={() => navigate(`/user/${user.id}/edit`)} disabled={!isEdit}>
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" placement="top">
                        <IconButton color="info" onClick={() => remove(`${user.id}`)} disabled={!isEdit}>
                          <BsTrash style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Definir acessos" placement="top">
                        <IconButton color="info" onClick={() => navigate(`/user-scope/${user.id}`)} disabled={!isEdit}>
                          <SiOpenaccess style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      {tenant?.useAcessUserTypeDocuments && (
                        <Tooltip title="Definir tipos de documentos" placement="top">
                          <IconButton color="info" onClick={() => openUserTypeDocumentsForm(user.id!, user.userName!)}>
                            <IoNewspaper style={{ fontSize: '15px' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        totalRows={totalRows}
        currentPage={currentPage}
        perPage={perPage}
        handleRequest={listUsers}
        setPerPage={setPerPage}
        setCurrentPage={setCurrentPage}
        orderField={orderField}
        orderDirection={orderDirection}
      />
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog open={openUserTypeDocuments} fullWidth TransitionComponent={Transition}>
        <InternalUserTypeDocumentsAccess
          closeDialog={() => {
            setOpenUserTypeDocuments(false);
          }}
          isOpen={openUserTypeDocuments}
          userId={userIdForDialog}
          userName={userNameIdForDialog}
        />
      </Dialog>
    </Container>
  );
};

export default InternalUserList;